import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import Breadcrumbs from '@/components/navigation/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import BookingDateTimeField from '@/components/bookings/BookingDateTimeField';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  Calendar as CalendarIcon,
  MoreVertical,
  CheckCircle2,
  CircleSlash,
  Pencil,
  Trash2,
  Clock,
  X,
  Building2,
  User,
  Mail,
  Phone
} from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, parseISO, addWeeks, subWeeks } from 'date-fns';
import { nl } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIEWS = [
  { value: 'day', label: 'Dag' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Maand' },
];

const DUTCH_WEEKDAYS = [
  'zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'
];

const getMonthlyLastWeekdayLabel = (startTime) => {
  if (!startTime) return 'Maandelijks op de laatste weekdag';
  return `Maandelijks op de laatste ${DUTCH_WEEKDAYS[new Date(startTime).getDay()]}`;
};

const BOOKING_STATUS_LABELS = {
  pending: 'Wacht op goedkeuring',
  confirmed: 'Bevestigd',
  cancelled: 'Geannuleerd',
  completed: 'Afgerond',
};

const BookingsPage = () => {
  const { user, hasCapability } = useAuth();
  const canCreateBookings = hasCapability('reservations.create');
  const canManageBookings = hasCapability('reservations.manage');
  const canDeleteBookings = hasCapability('reservations.delete');
  const canApproveBookings = hasCapability('reservations.approve');
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [resources, setResources] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [rejectDialogBooking, setRejectDialogBooking] = useState(null);
  const [deleteDialogBooking, setDeleteDialogBooking] = useState(null);
  const [editScope, setEditScope] = useState('single');
  const [rejectionReasonInput, setRejectionReasonInput] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    resource_id: '',
    start_time: '',
    end_time: '',
    description: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: '',
    recurring_enabled: false,
    recurrence_frequency: 'weekly',
    recurrence_occurrences: 4
  });

  const fetchData = useCallback(async () => {
    try {
      const [bookingsRes, resourcesRes, customersRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/resources`),
        axios.get(`${API}/customers`).catch(() => ({ data: [] }))
      ]);
      setBookings(bookingsRes.data);
      setResources(resourcesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Kon gegevens niet laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.resource_id || !formData.start_time || !formData.end_time) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    const startAt = new Date(formData.start_time);
    const endAt = new Date(formData.end_time);
    if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) {
      toast.error('Kies een geldige start- en einddatum met tijd');
      return;
    }
    if (endAt <= startAt) {
      toast.error('De einddatum en -tijd moeten na de start liggen');
      return;
    }
    if (!editingBooking && formData.recurring_enabled) {
      const occurrences = Number(formData.recurrence_occurrences);
      if (!Number.isInteger(occurrences) || occurrences < 2 || occurrences > 52) {
        toast.error('Kies een aantal herhalingen tussen 2 en 52');
        return;
      }
      if (formData.recurrence_frequency === 'monthly_last_weekday') {
        const startDate = new Date(formData.start_time);
        const followingWeek = new Date(startDate);
        followingWeek.setDate(followingWeek.getDate() + 7);
        if (followingWeek.getMonth() === startDate.getMonth()) {
          toast.error('Kies als startdatum de laatste gewenste weekdag van de maand');
          return;
        }
      }
    }

    try {
      const data = {
        title: formData.title,
        resource_id: formData.resource_id,
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
        description: formData.description,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        notes: formData.notes
      };

      if (editingBooking) {
        await axios.put(`${API}/bookings/${editingBooking.id}`, data, {
          params: { scope: editScope },
        });
        toast.success(editScope === 'series' ? 'Hele reeks bijgewerkt' : 'Boeking bijgewerkt');
      } else if (formData.recurring_enabled) {
        const response = await axios.post(`${API}/bookings/recurring`, {
          booking: data,
          frequency: formData.recurrence_frequency,
          occurrences: Number(formData.recurrence_occurrences)
        });
        toast.success(`${response.data.count} terugkerende boekingen aangemaakt`);
      } else {
        await axios.post(`${API}/bookings`, data);
        toast.success('Boeking aangemaakt');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      if (error.response?.status === 409) {
        const conflictStart = error.response?.data?.detail?.conflict_start_time;
        toast.error(conflictStart ? 'Een van de herhalingen overlapt met een bestaande boeking' : 'Dit tijdslot is al geboekt');
      } else {
        toast.error('Actie mislukt');
      }
    }
  };

  const handleDelete = (id) => {
    const booking = bookings.find((item) => item.id === id) || viewingBooking || editingBooking;
    if (booking) setDeleteDialogBooking(booking);
  };

  const confirmDelete = async (scope = 'single') => {
    if (!deleteDialogBooking) return;
    const id = deleteDialogBooking.id;
    try {
      await axios.delete(`${API}/bookings/${id}`, { params: { scope } });
      toast.success(scope === 'series' ? 'Hele reeks verwijderd' : 'Boeking verwijderd');
      setDeleteDialogBooking(null);
      if (editingBooking?.id === id) {
        setDialogOpen(false);
        resetForm();
      }
      if (viewingBooking?.id === id) setViewingBooking(null);
      fetchData();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const updateBookingStatus = async (booking, status, rejectionReason) => {
    try {
      const payload = { status };
      if (status === 'cancelled' && rejectionReason) {
        payload.rejection_reason = rejectionReason;
      }
      await axios.put(`${API}/bookings/${booking.id}`, payload);
      toast.success(status === 'confirmed' ? 'Boeking goedgekeurd' : 'Boeking afgewezen');
      if (editingBooking?.id === booking.id) {
        setDialogOpen(false);
      }
      if (viewingBooking?.id === booking.id) setViewingBooking(null);
      fetchData();
    } catch (error) {
      toast.error('Status wijzigen mislukt');
    }
  };

  const openRejectDialog = (booking) => {
    setRejectDialogBooking(booking);
    setRejectionReasonInput('');
  };

  const confirmRejectBooking = async () => {
    if (!rejectDialogBooking) return;
    await updateBookingStatus(rejectDialogBooking, 'cancelled', rejectionReasonInput.trim());
    setRejectDialogBooking(null);
    setRejectionReasonInput('');
  };

  const openEditDialog = useCallback((booking) => {
    setViewingBooking(null);
    setEditingBooking(booking);
    setEditScope('single');
    const startTime = typeof booking.start_time === 'string' ? parseISO(booking.start_time) : new Date(booking.start_time);
    const endTime = typeof booking.end_time === 'string' ? parseISO(booking.end_time) : new Date(booking.end_time);
    
    setFormData({
      title: booking.title,
      resource_id: booking.resource_id,
      start_time: format(startTime, "yyyy-MM-dd'T'HH:mm"),
      end_time: format(endTime, "yyyy-MM-dd'T'HH:mm"),
      description: booking.description || '',
      customer_name: booking.customer_name || '',
      customer_email: booking.customer_email || '',
      customer_phone: booking.customer_phone || '',
      notes: booking.notes || '',
      recurring_enabled: false,
      recurrence_frequency: 'weekly',
      recurrence_occurrences: 4
    });
    setDialogOpen(true);
  }, []);

  const openBookingDetails = useCallback((booking) => {
    setViewingBooking(booking);
  }, []);

  useEffect(() => {
    if (loading) return;
    const bookingId = searchParams.get('bookingId');
    if (!bookingId || dialogOpen || viewingBooking) return;

    const booking = bookings.find(item => item.id === bookingId);
    if (!booking) {
      toast.error('Boeking niet gevonden');
      setSearchParams({});
      return;
    }

    const startTime = typeof booking.start_time === 'string' ? parseISO(booking.start_time) : new Date(booking.start_time);
    setCurrentDate(startTime);
    openBookingDetails(booking);
    setSearchParams({});
  }, [bookings, dialogOpen, loading, openBookingDetails, searchParams, setSearchParams, viewingBooking]);

  const openNewBookingDialog = (date, resourceId = '') => {
    if (!canCreateBookings) return;
    resetForm();
    const startTime = new Date(date);
    startTime.setHours(9, 0, 0, 0);
    const endTime = new Date(date);
    endTime.setHours(10, 0, 0, 0);
    
    setFormData({
      title: '',
      resource_id: resourceId,
      start_time: format(startTime, "yyyy-MM-dd'T'HH:mm"),
      end_time: format(endTime, "yyyy-MM-dd'T'HH:mm"),
      description: '',
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      notes: '',
      recurring_enabled: false,
      recurrence_frequency: 'weekly',
      recurrence_occurrences: 4
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingBooking(null);
    setSelectedSlot(null);
    setFormData({
      title: '',
      resource_id: '',
      start_time: '',
      end_time: '',
      description: '',
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      notes: '',
      recurring_enabled: false,
      recurrence_frequency: 'weekly',
      recurrence_occurrences: 4
    });
  };

  const navigateDate = (direction) => {
    if (view === 'day') {
      setCurrentDate(prev => addDays(prev, direction));
    } else if (view === 'week') {
      setCurrentDate(prev => direction > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1));
    } else {
      setCurrentDate(prev => direction > 0 ? addMonths(prev, 1) : subMonths(prev, 1));
    }
  };

  const goToToday = () => setCurrentDate(new Date());

  const getDateRange = () => {
    if (view === 'day') {
      return [currentDate];
    } else if (view === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    } else {
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      return eachDayOfInterval({ start, end });
    }
  };

  const getBookingsForDate = (date, resourceId = null) => {
    return bookings.filter(booking => {
      const bookingDate = typeof booking.start_time === 'string' 
        ? parseISO(booking.start_time) 
        : new Date(booking.start_time);
      const sameDay = isSameDay(bookingDate, date);
      if (resourceId) {
        return sameDay && booking.resource_id === resourceId;
      }
      return sameDay;
    });
  };

  const formatDateHeader = () => {
    if (view === 'day') {
      return format(currentDate, "EEEE d MMMM yyyy", { locale: nl });
    } else if (view === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(start, "d MMM", { locale: nl })} - ${format(end, "d MMM yyyy", { locale: nl })}`;
    } else {
      return format(currentDate, "MMMM yyyy", { locale: nl });
    }
  };

  const getResourceColor = (resourceId) => {
    const resource = resources.find(r => r.id === resourceId);
    return resource?.color || '#0d9488';
  };

  const getResourceName = (resourceId) => {
    const resource = resources.find(r => r.id === resourceId);
    return resource?.name || 'Onbekend';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-96 bg-slate-200 rounded-2xl"></div>
        </div>
      </DashboardLayout>
    );
  }

  const dateRange = getDateRange();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="bookings-page">
        <Breadcrumbs items={[{ label: 'Reserveringen', href: '/bookings' }, { label: 'Kalender' }]} />
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Kalender</h1>
            <p className="text-slate-500">Beheer alle boekingen</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* View Selector */}
            <div className="flex bg-slate-100 rounded-xl p-1">
              {VIEWS.map((v) => (
                <button
                  key={v.value}
                  onClick={() => setView(v.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    view === v.value 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
            
            {/* Navigation */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigateDate(-1)}
                className="rounded-xl"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={goToToday}
                className="rounded-xl"
              >
                Vandaag
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigateDate(1)}
                className="rounded-xl"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            
            {canCreateBookings && (
              <Button 
                onClick={() => openNewBookingDialog(currentDate)}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                data-testid="new-booking-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nieuwe boeking
              </Button>
            )}
          </div>
        </div>

        {/* Date Header */}
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-teal-600" />
          <span className="text-lg font-semibold text-slate-900 capitalize">
            {formatDateHeader()}
          </span>
        </div>

        {/* Calendar */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            {view === 'month' ? (
              // Month View
              <div>
                <div className="grid grid-cols-7 bg-slate-50">
                  {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map((day) => (
                    <div key={day} className="p-3 text-center text-sm font-medium text-slate-600 border-b border-slate-100">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {/* Add empty cells for days before the first of the month */}
                  {Array.from({ length: (startOfMonth(currentDate).getDay() + 6) % 7 }).map((_, i) => (
                    <div key={`empty-${i}`} className="h-28 border-r border-b border-slate-100 bg-slate-50/50" />
                  ))}
                  {dateRange.map((date) => {
                    const dayBookings = getBookingsForDate(date);
                    const isToday = isSameDay(date, new Date());
                    return (
                      <div 
                        key={date.toISOString()} 
                        className={`calendar-cell cursor-pointer ${isToday ? 'bg-teal-50/50' : ''}`}
                        onClick={() => openNewBookingDialog(date)}
                      >
                        <div className={`text-sm font-medium mb-1 ${
                          isToday ? 'w-7 h-7 bg-teal-600 text-white rounded-full flex items-center justify-center' : 'text-slate-700'
                        }`}>
                          {format(date, 'd')}
                        </div>
                        <div className="space-y-1 overflow-hidden">
                          {dayBookings.slice(0, 3).map((booking) => (
                            <div
                              key={booking.id}
                              className="calendar-event text-white"
                              style={{ backgroundColor: getResourceColor(booking.resource_id) }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openBookingDetails(booking);
                              }}
                            >
                              {booking.title}
                            </div>
                          ))}
                          {dayBookings.length > 3 && (
                            <div className="text-xs text-slate-500 pl-2">
                              +{dayBookings.length - 3} meer
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : view === 'week' ? (
              // Week View
              <div className="overflow-x-auto">
                <div className="min-w-[800px]">
                  <div className="grid grid-cols-8 bg-slate-50 border-b border-slate-100">
                    <div className="p-3 text-sm font-medium text-slate-600">Resource</div>
                    {dateRange.map((date) => {
                      const isToday = isSameDay(date, new Date());
                      return (
                        <div key={date.toISOString()} className="p-3 text-center border-l border-slate-100">
                          <div className="text-xs text-slate-500 uppercase">
                            {format(date, 'EEE', { locale: nl })}
                          </div>
                          <div className={`text-lg font-semibold ${
                            isToday ? 'text-teal-600' : 'text-slate-900'
                          }`}>
                            {format(date, 'd')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {resources.length > 0 ? resources.map((resource) => (
                    <div key={resource.id} className="grid grid-cols-8 border-b border-slate-100">
                      <div className="p-3 flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: resource.color }}
                        />
                        <span className="text-sm font-medium text-slate-700 truncate">
                          {resource.name}
                        </span>
                      </div>
                      {dateRange.map((date) => {
                        const dayBookings = getBookingsForDate(date, resource.id);
                        return (
                          <div 
                            key={date.toISOString()} 
                            className="p-2 border-l border-slate-100 min-h-[80px] hover:bg-slate-50 cursor-pointer"
                            onClick={() => openNewBookingDialog(date, resource.id)}
                          >
                            {dayBookings.map((booking) => {
                              const startTime = typeof booking.start_time === 'string' 
                                ? parseISO(booking.start_time) 
                                : new Date(booking.start_time);
                              return (
                                <div
                                  key={booking.id}
                                  className="text-xs p-2 rounded-lg mb-1 text-white cursor-pointer hover:opacity-90"
                                  style={{ backgroundColor: resource.color }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openBookingDetails(booking);
                                  }}
                                >
                                  <div className="font-medium truncate">{booking.title}</div>
                                  <div className="opacity-80">{format(startTime, 'HH:mm')}</div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )) : (
                    <div className="p-8 text-center text-slate-500">
                      Voeg eerst resources toe om boekingen te kunnen maken
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Day View
              <div className="p-4">
                <div className="space-y-4">
                  {resources.length > 0 ? resources.map((resource) => {
                    const dayBookings = getBookingsForDate(currentDate, resource.id);
                    return (
                      <div key={resource.id} className="border border-slate-100 rounded-xl overflow-hidden">
                        <div className="bg-slate-50 px-4 py-3 flex items-center gap-2 border-b border-slate-100">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: resource.color }}
                          />
                          <span className="font-medium text-slate-700">{resource.name}</span>
                          <span className="text-sm text-slate-500">({dayBookings.length} boekingen)</span>
                        </div>
                        <div className="p-4">
                          {dayBookings.length > 0 ? (
                            <div className="space-y-2">
                              {dayBookings.map((booking) => {
                                const startTime = typeof booking.start_time === 'string' 
                                  ? parseISO(booking.start_time) 
                                  : new Date(booking.start_time);
                                const endTime = typeof booking.end_time === 'string' 
                                  ? parseISO(booking.end_time) 
                                  : new Date(booking.end_time);
                                return (
                                  <div
                                    key={booking.id}
                                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 cursor-pointer group"
                                    onClick={() => openBookingDetails(booking)}
                                  >
                                    <div 
                                      className="w-1 h-12 rounded-full"
                                      style={{ backgroundColor: resource.color }}
                                    />
                                    <div className="flex-1">
                                      <div className="font-medium text-slate-900 flex items-center gap-2">
                                        <span>{booking.title}</span>
                                        {booking.status === 'pending' && (
                                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                                            Wacht op goedkeuring
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-sm text-slate-500 flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {format(startTime, 'HH:mm')} - {format(endTime, 'HH:mm')}
                                      </div>
                                    </div>
                                    {booking.customer_name && (
                                      <div className="text-sm text-slate-500">{booking.customer_name}</div>
                                    )}
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          className="opacity-0 group-hover:opacity-100"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <MoreVertical className="w-4 h-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        {canApproveBookings && booking.status === 'pending' && (
                                          <>
                                            <DropdownMenuItem
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                updateBookingStatus(booking, 'confirmed');
                                              }}
                                            >
                                              <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />
                                              Goedkeuren
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openRejectDialog(booking);
                                              }}
                                              className="text-amber-700"
                                            >
                                              <CircleSlash className="w-4 h-4 mr-2" />
                                              Afwijzen
                                            </DropdownMenuItem>
                                          </>
                                        )}
                                        {canManageBookings && (
                                          <DropdownMenuItem onClick={() => openEditDialog(booking)}>
                                            <Pencil className="w-4 h-4 mr-2" />
                                            Bewerken
                                          </DropdownMenuItem>
                                        )}
                                        {canDeleteBookings && (
                                          <DropdownMenuItem 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDelete(booking.id);
                                            }}
                                            className="text-red-600"
                                          >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Verwijderen
                                          </DropdownMenuItem>
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div 
                              className="text-center py-6 text-slate-400 hover:bg-slate-50 rounded-xl cursor-pointer"
                              onClick={() => openNewBookingDialog(currentDate, resource.id)}
                            >
                              <Plus className="w-5 h-5 mx-auto mb-1" />
                              <span className="text-sm">Klik om te boeken</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="p-8 text-center text-slate-500">
                      Voeg eerst resources toe om boekingen te kunnen maken
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Series delete choice */}
        <Dialog open={!!deleteDialogBooking} onOpenChange={(open) => { if (!open) setDeleteDialogBooking(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Boeking verwijderen</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              {deleteDialogBooking?.recurrence_group_id
                ? 'Deze boeking maakt deel uit van een terugkerende reeks. Wat wil je verwijderen?'
                : 'Weet je zeker dat je deze boeking wilt verwijderen?'}
            </p>
            <DialogFooter className="gap-2 sm:justify-end">
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setDeleteDialogBooking(null)}>
                Annuleren
              </Button>
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => confirmDelete('single')}>
                {deleteDialogBooking?.recurrence_group_id ? 'Alleen deze afspraak' : 'Verwijderen'}
              </Button>
              {deleteDialogBooking?.recurrence_group_id && (
                <Button type="button" className="rounded-xl bg-red-600 text-white hover:bg-red-700" onClick={() => confirmDelete('series')}>
                  Hele reeks
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Booking details */}
        <Dialog
          open={!!viewingBooking}
          onOpenChange={(open) => {
            if (!open) setViewingBooking(null);
          }}
        >
          <DialogContent className="sm:max-w-lg">
            {viewingBooking && (() => {
              const startTime = typeof viewingBooking.start_time === 'string'
                ? parseISO(viewingBooking.start_time)
                : new Date(viewingBooking.start_time);
              const endTime = typeof viewingBooking.end_time === 'string'
                ? parseISO(viewingBooking.end_time)
                : new Date(viewingBooking.end_time);
              const resource = resources.find((item) => item.id === viewingBooking.resource_id);

              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="pr-8">{viewingBooking.title}</DialogTitle>
                  </DialogHeader>

                  <div className="space-y-4">
                    <div className="rounded-xl bg-slate-50 p-4 space-y-3 text-sm">
                      <div className="flex items-start gap-3">
                        <CalendarIcon className="w-4 h-4 mt-0.5 text-teal-600" />
                        <div>
                          <p className="font-medium text-slate-900 capitalize">
                            {format(startTime, 'EEEE d MMMM yyyy', { locale: nl })}
                          </p>
                          <p className="text-slate-600">
                            {format(startTime, 'HH:mm')} – {format(endTime, 'HH:mm')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Building2 className="w-4 h-4 text-teal-600" />
                        <span>{resource?.name || 'Onbekende resource'}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-4 h-4 flex items-center justify-center">
                          <span className={`w-2.5 h-2.5 rounded-full ${
                            viewingBooking.status === 'confirmed' ? 'bg-emerald-500' :
                            viewingBooking.status === 'cancelled' ? 'bg-red-500' :
                            viewingBooking.status === 'completed' ? 'bg-slate-500' : 'bg-amber-500'
                          }`} />
                        </span>
                        <span>{BOOKING_STATUS_LABELS[viewingBooking.status] || viewingBooking.status}</span>
                      </div>
                    </div>

                    {(viewingBooking.customer_name || viewingBooking.customer_email || viewingBooking.customer_phone) && (
                      <div className="space-y-2">
                        <Label>Klantgegevens</Label>
                        {viewingBooking.customer_name && <p className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-slate-400" />{viewingBooking.customer_name}</p>}
                        {viewingBooking.customer_email && <p className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-slate-400" />{viewingBooking.customer_email}</p>}
                        {viewingBooking.customer_phone && <p className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-slate-400" />{viewingBooking.customer_phone}</p>}
                      </div>
                    )}

                    {viewingBooking.description && (
                      <div>
                        <Label>Beschrijving</Label>
                        <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">{viewingBooking.description}</p>
                      </div>
                    )}
                    {viewingBooking.notes && (
                      <div>
                        <Label>Interne notities</Label>
                        <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">{viewingBooking.notes}</p>
                      </div>
                    )}
                  </div>

                  <DialogFooter className="gap-2 sm:justify-between">
                    {canDeleteBookings ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(viewingBooking.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Verwijderen
                      </Button>
                    ) : <span />}
                    <div className="flex flex-wrap justify-end gap-2">
                      {canApproveBookings && viewingBooking.status === 'pending' && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            className="rounded-xl border-amber-200 text-amber-700 hover:bg-amber-50"
                            onClick={() => openRejectDialog(viewingBooking)}
                          >
                            <CircleSlash className="w-4 h-4 mr-2" />
                            Afwijzen
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => updateBookingStatus(viewingBooking, 'confirmed')}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Goedkeuren
                          </Button>
                        </>
                      )}
                      <Button type="button" variant="outline" className="rounded-xl" onClick={() => setViewingBooking(null)}>
                        Sluiten
                      </Button>
                      {canManageBookings && (
                        <Button type="button" className="rounded-xl bg-teal-600 hover:bg-teal-700" onClick={() => openEditDialog(viewingBooking)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Bewerken
                        </Button>
                      )}
                    </div>
                  </DialogFooter>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Booking create/edit dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="w-[calc(100%-1rem)] max-h-[calc(100dvh-2rem)] max-w-lg overflow-y-auto rounded-2xl p-4 sm:max-h-[90vh] sm:p-6">
            <DialogHeader>
              <DialogTitle>
                {editingBooking ? 'Boeking bewerken' : 'Nieuwe boeking'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titel *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Bijv. Vergadering Team A"
                  className="rounded-xl"
                  data-testid="booking-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="resource">Resource *</Label>
                <Select 
                  value={formData.resource_id} 
                  onValueChange={(value) => setFormData({ ...formData, resource_id: value })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Selecteer een resource" />
                  </SelectTrigger>
                  <SelectContent>
                    {resources.map((resource) => (
                      <SelectItem key={resource.id} value={resource.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: resource.color }}
                          />
                          <span>{resource.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                <BookingDateTimeField
                  id="start_time"
                  label="Start"
                  value={formData.start_time}
                  onChange={(start_time) => setFormData((previous) => ({ ...previous, start_time }))}
                  defaultTime="09:00"
                />
                <BookingDateTimeField
                  id="end_time"
                  label="Einde"
                  value={formData.end_time}
                  onChange={(end_time) => setFormData((previous) => ({ ...previous, end_time }))}
                  defaultTime="10:00"
                />
              </div>

              {editingBooking?.recurrence_group_id && (
                <div className="space-y-2 rounded-xl border border-teal-100 bg-teal-50/60 p-4">
                  <Label htmlFor="edit-scope">Wijziging toepassen op</Label>
                  <Select value={editScope} onValueChange={setEditScope}>
                    <SelectTrigger id="edit-scope" className="rounded-xl bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Alleen deze afspraak</SelectItem>
                      <SelectItem value="series">De hele reeks</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-600">
                    Bij de hele reeks blijft iedere datum behouden; een nieuwe tijd wordt op alle afspraken toegepast.
                  </p>
                </div>
              )}

              {!editingBooking && (
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-4">
                  <label className="flex items-center gap-3 text-sm font-medium text-slate-800">
                    <input
                      type="checkbox"
                      checked={formData.recurring_enabled}
                      onChange={(e) => setFormData({ ...formData, recurring_enabled: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    Terugkerende activiteit
                  </label>

                  {formData.recurring_enabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="recurrence_frequency">Herhaling</Label>
                        <Select
                          value={formData.recurrence_frequency}
                          onValueChange={(value) => setFormData({ ...formData, recurrence_frequency: value })}
                        >
                          <SelectTrigger id="recurrence_frequency" className="rounded-xl bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Dagelijks</SelectItem>
                            <SelectItem value="weekly">Wekelijks</SelectItem>
                            <SelectItem value="biweekly">Tweewekelijks</SelectItem>
                            <SelectItem value="monthly">Maandelijks</SelectItem>
                            <SelectItem value="monthly_last_weekday">
                              {getMonthlyLastWeekdayLabel(formData.start_time)}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="recurrence_occurrences">Aantal keer</Label>
                        <Input
                          id="recurrence_occurrences"
                          type="number"
                          min="2"
                          max="52"
                          value={formData.recurrence_occurrences}
                          onChange={(e) => setFormData({ ...formData, recurrence_occurrences: e.target.value })}
                          className="rounded-xl bg-white"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">Beschrijving</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optionele beschrijving..."
                  className="rounded-xl resize-none"
                  rows={2}
                />
              </div>

              <div className="border-t border-slate-100 pt-4">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Klantgegevens (optioneel)</Label>
                <div className="space-y-3">
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    placeholder="Naam klant"
                    className="rounded-xl"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      placeholder="E-mail"
                      className="rounded-xl"
                    />
                    <Input
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      placeholder="Telefoon"
                      className="rounded-xl"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notities</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Interne notities..."
                  className="rounded-xl resize-none"
                  rows={2}
                />
              </div>

              <DialogFooter className="gap-2 sm:justify-between">
                {editingBooking && (
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(editingBooking.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Verwijderen
                  </Button>
                )}
                {canApproveBookings && editingBooking?.status === 'pending' && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => updateBookingStatus(editingBooking, 'confirmed')}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Goedkeuren
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-xl border-amber-200 text-amber-700 hover:bg-amber-50"
                      onClick={() => openRejectDialog(editingBooking)}
                    >
                      <CircleSlash className="w-4 h-4 mr-2" />
                      Afwijzen
                    </Button>
                  </>
                )}
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  className="rounded-xl"
                >
                  Annuleren
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                  data-testid="save-booking-btn"
                >
                  {editingBooking ? 'Opslaan' : formData.recurring_enabled ? 'Reeks maken' : 'Boeking maken'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reject booking dialog */}
        <Dialog open={!!rejectDialogBooking} onOpenChange={(open) => { if (!open) setRejectDialogBooking(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Boeking afwijzen</DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Reden (optioneel)</Label>
              <Textarea
                id="rejection-reason"
                value={rejectionReasonInput}
                onChange={(e) => setRejectionReasonInput(e.target.value)}
                placeholder="Deze reden wordt meegestuurd in de e-mail naar de aanvrager"
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setRejectDialogBooking(null)}>
                Annuleren
              </Button>
              <Button
                type="button"
                className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
                onClick={confirmRejectBooking}
              >
                <CircleSlash className="w-4 h-4 mr-2" />
                Afwijzen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default BookingsPage;
